import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { adminApi } from '../../lib/api';
import { 
  Users, CheckSquare, Timer, TrendingUp, Crown, Shield,
  ArrowRight, BarChart3, Settings, Loader2, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Admin stats error:', error);
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
            Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Welcome back, {user?.name}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card" data-testid="stat-total-users">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F0F9FF] dark:bg-[#4A9CC7]/15 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#4A9CC7]" />
                </div>
                <div>
                  <p className="stat-value">{stats?.users?.total || 0}</p>
                  <p className="stat-label">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-premium-users">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-50 dark:bg-yellow-500/15 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="stat-value text-yellow-600">{stats?.users?.premium || 0}</p>
                  <p className="stat-label">Premium Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-total-tasks">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-500/15 flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="stat-value">{stats?.tasks?.total || 0}</p>
                  <p className="stat-label">Total Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-focus-hours">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-500/15 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="stat-value">{stats?.focus?.total_hours || 0}h</p>
                  <p className="stat-label">Focus Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* User Stats Card */}
          <Card data-testid="user-stats-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#4A9CC7]" />
                User Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Active Users</span>
                  <span className="font-semibold">{stats?.users?.active || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Free Tier</span>
                  <span className="font-semibold text-blue-600">{stats?.users?.free || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Premium Tier</span>
                  <span className="font-semibold text-yellow-600">{stats?.users?.premium || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Admins</span>
                  <span className="font-semibold text-purple-600">{stats?.users?.admins || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">New (Last 30 days)</span>
                  <span className="font-semibold text-green-600">+{stats?.users?.new_last_30_days || 0}</span>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/admin/users')}
                data-testid="view-users-btn"
              >
                Manage Users <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Task Stats Card */}
          <Card data-testid="task-stats-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-[#4A9CC7]" />
                Task Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Tasks</span>
                  <span className="font-semibold">{stats?.tasks?.total || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completed</span>
                  <span className="font-semibold text-green-600">{stats?.tasks?.completed || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completion Rate</span>
                  <span className="font-semibold">{stats?.tasks?.completion_rate || 0}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Recurring Tasks</span>
                  <span className="font-semibold">{stats?.tasks?.recurring || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Created Today</span>
                  <span className="font-semibold text-blue-600">{stats?.tasks?.created_today || 0}</span>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/admin/reports')}
                data-testid="view-reports-btn"
              >
                View Reports <BarChart3 className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card data-testid="quick-actions">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => navigate('/admin/users')}
              >
                <Users className="w-6 h-6 mb-2 text-[#4A9CC7]" />
                Manage Users
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => navigate('/admin/reports')}
              >
                <BarChart3 className="w-6 h-6 mb-2 text-green-500" />
                View Reports
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => navigate('/admin/features')}
              >
                <Settings className="w-6 h-6 mb-2 text-purple-500" />
                Feature Flags
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => navigate('/admin/settings')}
              >
                <Shield className="w-6 h-6 mb-2 text-orange-500" />
                App Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
